// src/pages/designer/DesignerEarnings.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getCommissions, getSettings, commissionRateForDesignerProfile } from "../../lib/db";
import { StatCard, TableCard, Table, Badge, DetailCard, SectionLabel, Spinner, fmt$, fmtDate, CommissionPctFigures } from "../../components/UI";

export default function DesignerEarnings() {
  const pagePad = "var(--page-pad-x)";
  const { profile } = useAuth();
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!profile?.uid) return undefined;
    let cancelled = false;
    setLoadError("");
    setLoading(true);
    Promise.all([getCommissions(profile.uid), getSettings()])
      .then(([c, s]) => {
        if (!cancelled) {
          setHistory(c);
          setSettings(s);
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setLoadError("Could not load payment history or settings. Try refreshing.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [profile?.uid]);

  const effectiveRate =
    profile && settings ? commissionRateForDesignerProfile(profile, settings) : null;
  const commissionRateCell = loading ? (
    <span style={{ color: "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 14 }}>Loading…</span>
  ) : effectiveRate == null ? (
    "Pending"
  ) : (
    <CommissionPctFigures rate={effectiveRate} fontSize={18} color="var(--charcoal)" />
  );

  const ytdEarned = profile?.ytdEarned || 0;
  const pending   = profile?.pendingPayout || 0;
  const paid      = profile?.totalPaid || 0;

  const nextPayDate = (() => {
    const d = new Date(); d.setMonth(d.getMonth() + 1); d.setDate(1);
    return d.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});
  })();

  const cols = [
    { key:"date",    label:"Paid On" },
    { key:"amount",  label:"Amount" },
    { key:"status",  label:"Status" },
  ];

  const rows = history.map(c => ({
    date:   fmtDate(c.paidAt),
    amount: <span style={{color:"var(--green)",fontWeight:400}}>{fmt$(c.amount)}</span>,
    status: <Badge label="paid" />,
  }));

  return (
    <div>
      <div style={{ padding:`28px ${pagePad} 0` }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300 }}>My Earnings</h1>
        <p style={{ fontSize:13, color:"var(--mid)", marginTop:4 }}>Commission history and upcoming payouts</p>
      </div>
      <div style={{ padding:`var(--page-pad-y) ${pagePad} 32px` }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:16, marginBottom:24 }}>
          <StatCard label="Total Earned YTD" value={fmt$(ytdEarned)} />
          <StatCard label="Pending Payout"   value={fmt$(pending)} accent />
          <StatCard label="Total Paid Out"   value={fmt$(paid)} />
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16, marginBottom:24 }}>
          <DetailCard title="Upcoming Payout" rows={[
            ["Amount",       <span style={{color:"var(--gold)",fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:300}}>{fmt$(pending)}</span>],
            ["Scheduled",    nextPayDate],
            ["Minimum",      fmt$(250) + " threshold"],
            ["Method",       "Check / ACH on file"],
          ]} />
          <DetailCard title="Year Summary" rows={[
            ["Total Sales Referred", fmt$(profile?.ytdSales)],
            ["Commission Rate", commissionRateCell],
            ["Earned This Year",     fmt$(ytdEarned)],
            ["Paid Out",             fmt$(paid)],
            ["Still Pending",        fmt$(pending)],
          ]} />
        </div>

        <SectionLabel>Payment History</SectionLabel>
        <TableCard>
          {loading ? (
            <Spinner />
          ) : loadError ? (
            <div style={{ padding: "36px 24px", fontSize: 14, color: "var(--mid)", textAlign: "center" }}>{loadError}</div>
          ) : (
            <Table cols={cols} rows={rows} emptyMsg="No payments yet — payouts occur on the 1st of each month." />
          )}
        </TableCard>
      </div>
    </div>
  );
}
